
import React from 'react';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { format } from "date-fns";
import { Badge } from "@/components/ui/badge";

interface Mission {
  id: string;
  title: string;
  description: string | null;
  category: string;
  status: string | null;
  team_id: string;
  created_at: string;
  start_date: string | null;
  end_date: string | null;
  attachment_url: string | null;
  team_name?: string; // Add team_name property
}

interface MissionDetailsProps {
  mission: Mission;
}

export const MissionDetails = ({ mission }: MissionDetailsProps) => {
  const createdDate = new Date(mission.created_at);
  
  return (
    <SheetContent>
      <SheetHeader>
        <SheetTitle>{mission.title}</SheetTitle>
        <SheetDescription>
          <div className="flex flex-wrap gap-2 my-2">
            <Badge variant="outline">{mission.category}</Badge>
            {mission.status && (
              <Badge variant={mission.status === 'completed' ? 'default' : 'secondary'}>
                {mission.status}
              </Badge>
            )}
          </div>
        </SheetDescription>
      </SheetHeader>
      
      <div className="mt-6 space-y-4">
        {mission.team_name && (
          <div>
            <h3 className="text-sm font-medium">Team</h3>
            <p className="text-sm text-muted-foreground">{mission.team_name}</p>
          </div>
        )}
        
        <div>
          <h3 className="text-sm font-medium">Created</h3>
          <p className="text-sm text-muted-foreground">
            {format(createdDate, "MMMM d, yyyy 'at' h:mm a")}
          </p>
        </div>
        
        {mission.description && (
          <div>
            <h3 className="text-sm font-medium">Description</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {mission.description}
            </p>
          </div>
        )}
        
        {mission.start_date && (
          <div>
            <h3 className="text-sm font-medium">Start Date</h3>
            <p className="text-sm text-muted-foreground">
              {format(fromLocalISOString(mission.start_date), "MMMM d, yyyy")}
            </p>
          </div>
        )}
        
        {mission.end_date && (
          <div>
            <h3 className="text-sm font-medium">End Date</h3>
            <p className="text-sm text-muted-foreground">
              {format(fromLocalISOString(mission.end_date), "MMMM d, yyyy")}
            </p>
          </div>
        )}
        
        {mission.attachment_url && (
          <div>
            <h3 className="text-sm font-medium mb-2">Attachment</h3>
            <div className="rounded-lg overflow-hidden border">
              <img 
                src={mission.attachment_url} 
                alt={mission.title}
                className="w-full h-auto object-contain"
                onError={(e) => {
                  console.error('Failed to load mission image:', mission.attachment_url);
                }}
                onLoad={() => console.log('Mission image loaded successfully:', mission.attachment_url)}
              />
            </div>
            <a 
              href={mission.attachment_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-primary hover:underline mt-2 inline-block"
            >
              Open in new tab
            </a>
          </div>
        )}
      </div>
    </SheetContent>
  );
};
